import { useState } from 'react';
import { ArrowLeft, Volume2, Star, Eraser, Play } from 'lucide-react';
import { arabicAlphabet } from '../data/arabicAlphabet';
import confetti from 'canvas-confetti';

interface ArabicLearningProps {
  onBack: () => void;
  isKidsMode?: boolean;
}

export function ArabicLearning({ onBack, isKidsMode = false }: ArabicLearningProps) {
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [word, setWord] = useState<string>('');
  const [isPlayingWord, setIsPlayingWord] = useState(false);

  const handlePlaySound = (id: string, char: string) => {
    // Append to word
    setWord(prev => prev + char);

    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      
      const utterance = new SpeechSynthesisUtterance(char);
      utterance.lang = 'ar-SA';
      utterance.rate = 0.8;
      
      utterance.onstart = () => setPlayingId(id);
      utterance.onend = () => setPlayingId(null);
      utterance.onerror = () => setPlayingId(null);
      
      window.speechSynthesis.speak(utterance);

      // Fun interaction for kids
      if (isKidsMode) {
        confetti({
          particleCount: 15,
          spread: 30,
          origin: { y: 0.7 },
          colors: ['#FCD34D', '#34D399', '#60A5FA']
        });
      }
    }
  };

  const handlePlayWord = () => {
    if (!word || !('speechSynthesis' in window)) return;
    
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(word);
    utterance.lang = 'ar-SA';
    utterance.rate = 0.7; // Slower for whole words
    
    utterance.onstart = () => setIsPlayingWord(true);
    utterance.onend = () => setIsPlayingWord(false);
    
    window.speechSynthesis.speak(utterance);
  };

  return (
    <div className={`min-h-screen ${isKidsMode ? 'bg-sky-50 font-comic' : 'bg-slate-50 font-sans'} flex flex-col pb-24`}>
      {/* Header */}
      <div className={`px-6 py-4 pt-safe-top sticky top-0 z-10 shadow-sm flex items-center justify-between ${isKidsMode ? 'bg-white border-b-4 border-sky-200' : 'bg-white border-b border-slate-200'}`}>
        <button 
          onClick={onBack} 
          className={`p-2 -ml-2 rounded-full transition-colors ${isKidsMode ? 'bg-yellow-100 hover:bg-yellow-200 text-yellow-600' : 'hover:bg-slate-100 text-slate-600'}`}
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <div className="text-center">
          <h2 className={`text-2xl font-bold flex items-center justify-center gap-2 ${isKidsMode ? 'text-sky-600' : 'text-slate-800'}`}>
            Learn Arabic {isKidsMode && <Star className="w-6 h-6 text-yellow-400 fill-yellow-400 animate-spin-slow" />}
          </h2>
          <p className={`${isKidsMode ? 'text-sky-400' : 'text-slate-500'} text-sm font-medium`}>
            {isKidsMode ? 'Build your name!' : 'Word Builder'}
          </p>
        </div>
        <div className="w-10"></div>
      </div>

      {/* Word Builder Display */}
      <div className="px-6 pt-4 pb-2">
        <div className={`w-full min-h-[120px] rounded-3xl flex flex-col items-center justify-center p-6 transition-all ${
          isKidsMode 
            ? 'bg-white border-4 border-dashed border-sky-200 shadow-inner' 
            : 'bg-white border border-slate-200 shadow-sm'
        }`}>
          {word ? (
             <span className={`text-6xl md:text-8xl font-serif text-center mb-4 dir-rtl ${isKidsMode ? 'text-sky-600' : 'text-slate-800'}`}>
               {word}
             </span>
          ) : (
             <span className="text-slate-300 text-xl font-medium italic">
               {isKidsMode ? 'Tap letters below to write!' : 'Tap letters to build a word...'}
             </span>
          )}

          <div className="flex gap-3 mt-auto">
             <button
               onClick={() => setWord(prev => prev.slice(0, -1))}
               disabled={!word}
               className={`p-3 rounded-full transition-colors ${
                 !word ? 'opacity-50 cursor-not-allowed' : 'hover:bg-red-50 text-red-500'
               }`}
               title="Backspace"
             >
               <Eraser className="w-6 h-6" />
             </button>
             
             <button
               onClick={handlePlayWord}
               disabled={!word}
               className={`px-6 py-2 rounded-full font-bold flex items-center gap-2 transition-all ${
                 !word 
                   ? 'bg-slate-100 text-slate-400 cursor-not-allowed' 
                   : isPlayingWord
                     ? 'bg-emerald-500 text-white scale-95'
                     : isKidsMode
                       ? 'bg-yellow-400 text-yellow-900 hover:bg-yellow-300 shadow-md hover:scale-105'
                       : 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm'
               }`}
             >
               <Play className={`w-5 h-5 ${isPlayingWord ? 'animate-pulse' : ''}`} />
               {isKidsMode ? 'Read It!' : 'Pronounce'}
             </button>
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="flex-1 p-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 pb-20">
        {arabicAlphabet.map((letter) => (
          <div 
            key={letter.id}
            onClick={() => handlePlaySound(letter.id, letter.char)}
            className={`
              relative group cursor-pointer transition-all duration-300
              ${isKidsMode 
                ? `${letter.color} rounded-3xl border-b-8 active:border-b-0 active:translate-y-2 hover:scale-105 shadow-xl` 
                : 'bg-white hover:bg-slate-50 border border-slate-200 rounded-xl hover:shadow-md'
              }
              flex flex-col items-center justify-center p-6 h-40
            `}
          >
            <span className={`text-6xl mb-2 font-serif ${playingId === letter.id ? 'animate-bounce' : ''}`}>
              {letter.char}
            </span>
            
            <div className="flex items-center gap-2 mt-auto">
              <span className={`font-bold ${isKidsMode ? 'text-lg' : 'text-sm text-slate-500'}`}>
                {letter.name}
              </span>
              <Volume2 className={`w-4 h-4 ${playingId === letter.id ? 'animate-pulse text-current' : 'opacity-50'}`} />
            </div>

            {/* Shine effect for kids mode */}
            {isKidsMode && (
              <div className="absolute top-2 right-2 w-3 h-3 bg-white rounded-full opacity-40"></div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
